import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Swords, Gamepad2, Clock, DollarSign, ChevronRight, 
  Search, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import { useAlert } from '../components/CustomDialog';
import { AnnouncementSidebar } from '../components/AnnouncementDisplay';
import LobbyShoutbox from '../components/LobbyShoutbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_COLORS = {
  beginner: '#00FF94',
  intermediate: '#00F0FF',
  advanced: '#FFB800',
  professional: '#FF2E2E'
};

const MatchLobby = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showAlert, AlertDialog } = useAlert();
  const [openMatches, setOpenMatches] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [config, setConfig] = useState(null);
  const [acceptingMatch, setAcceptingMatch] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [matchesRes, gamesRes, configRes] = await Promise.all([
        axios.get(`${API}/matches/open`, { withCredentials: true }),
        axios.get(`${API}/matches/games`, { withCredentials: true }),
        axios.get(`${API}/matches/config`, { withCredentials: true })
      ]);
      setOpenMatches(matchesRes.data);
      setGames(gamesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptMatch = async (matchId) => {
    if (!user?.is_verified) {
      showAlert('Please accept terms and conditions first', 'error');
      return;
    }

    setAcceptingMatch(matchId);
    try {
      // Join the match (reserves it) then navigate to confirmation screen
      await axios.post(
        `${API}/matches/join`,
        { match_id: matchId },
        { withCredentials: true }
      );
      navigate(`/match/${matchId}/confirm`);
    } catch (error) {
      showAlert(error.response?.data?.detail || 'Failed to join match', 'error');
    } finally {
      setAcceptingMatch(null);
    }
  };

  const filteredMatches = openMatches.filter(match => {
    const gameMatch = selectedGame === 'all' || match.game_id === selectedGame;
    const searchMatch = match.creator_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       match.game_name.toLowerCase().includes(searchQuery.toLowerCase());
    return gameMatch && searchMatch;
  });

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <DashboardLayout>
      <AlertDialog />
      
      <div className="max-w-6xl mx-auto" data-testid="lobby-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">Competition Lobby</h1>
            <p className="text-white/50">Browse and join skill-based matches</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={fetchData}
              className="btn-secondary"
              data-testid="refresh-lobby-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/create-match')}
              className="btn-primary clip-slant-sm"
              data-testid="lobby-create-match-btn"
            >
              Create Competition
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search by player or game..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-dark"
                data-testid="lobby-search-input"
              />
            </div>
            <Select value={selectedGame} onValueChange={setSelectedGame}>
              <SelectTrigger className="w-full md:w-48 bg-white/5 border-white/10" data-testid="game-filter-select">
                <Filter className="w-4 h-4 mr-2 text-white/50" />
                <SelectValue placeholder="All Games" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0B] border-white/10">
                <SelectItem value="all">All Games</SelectItem>
                {games.map(game => (
                  <SelectItem key={game.id} value={game.id}>{game.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Content Grid with Sidebar */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Match List */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
        ) : filteredMatches.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <Swords className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h2 className="font-heading font-semibold text-xl mb-2">No Open Matches</h2>
            <p className="text-white/50 mb-6">
              {searchQuery || selectedGame !== 'all' 
                ? 'No matches found with your filters'
                : 'Be the first to create a challenge!'}
            </p>
            <Button 
              onClick={() => navigate('/create-match')}
              className="btn-primary clip-slant"
              data-testid="empty-lobby-create-btn"
            >
              Create a Match
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredMatches.map((match, i) => (
              <motion.div
                key={match.match_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-xl p-5 hover:border-white/20 transition-all mb-6"
                data-testid={`lobby-match-${match.match_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#7000FF]/20 flex items-center justify-center flex-shrink-0">
                      <Gamepad2 className="w-7 h-7 text-[#7000FF]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg">{match.game_name}</h3>
                      <p className="text-white/50 text-sm">
                        Challenged by <span className="text-white">{match.creator_name}</span>
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-white/40">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(match.created_at)}
                        </span>
                        {match.creator_skill_level && (
                          <span 
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ 
                              backgroundColor: `${SKILL_COLORS[match.creator_skill_level] || '#666'}20`,
                              color: SKILL_COLORS[match.creator_skill_level] || '#666'
                            }}
                          >
                            {match.creator_skill_level.charAt(0).toUpperCase() + match.creator_skill_level.slice(1)}
                          </span>
                        )}
                        {match.is_custom_game && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFB800]/10 text-[#FFB800]">
                            Custom Game
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-white/50 text-xs uppercase tracking-wide">Entry Cost</p>
                      <p 
                        className="font-mono text-2xl font-bold text-[#00F0FF]"
                        style={{ textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}
                      >
                        {(match.wager_amount * (config?.credit_conversion_rate || 100)).toLocaleString()} <span className="text-sm">credits</span>
                      </p>
                      <p className="text-xs text-[#CCFF00]/70">
                        Reward ${match.prize_amount?.toFixed(2) || ((match.wager_amount * 2) - (match.platform_fee * 2)).toFixed(2)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleAcceptMatch(match.match_id)}
                      disabled={acceptingMatch === match.match_id || (user?.credits_balance || 0) < match.wager_amount * (config?.credit_conversion_rate || 100)}
                      className="btn-primary clip-slant-sm px-6"
                      data-testid={`accept-match-${match.match_id}`}
                    >
                      {acceptingMatch === match.match_id ? (
                        'Joining...'
                      ) : (user?.credits_balance || 0) < match.wager_amount * (config?.credit_conversion_rate || 100) ? (
                        'Low Credits'
                      ) : (
                        <>
                          Join
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
          </div>

          {/* Sidebar with Announcements and Shoutbox */}
          <div className="hidden lg:block space-y-6">
            <LobbyShoutbox />
            <AnnouncementSidebar />
          </div>
        </div>
        
        {/* Mobile Shoutbox - Shows below matches on small screens */}
        <div className="lg:hidden mt-6">
          <LobbyShoutbox />
        </div>

        {/* Balance Warning */}
        {!loading && filteredMatches.length > 0 && (user?.credits_balance || 0) < 500 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/20"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-[#FFB800]" />
              <div>
                <p className="text-[#FFB800] font-medium">Low Credits</p>
                <p className="text-white/50 text-sm">
                  You have {(user?.credits_balance || 0).toLocaleString()} credits. 
                  <Button 
                    variant="link" 
                    className="text-[#00F0FF] p-0 h-auto ml-1"
                    onClick={() => navigate('/wallet')}
                  >
                    Purchase credits
                  </Button>
                  {' '}to enter matches.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MatchLobby;
